import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  Box,
  Color,
  ColorNames,
  ColorShades,
  DoughnutDataModel,
  GroupedBarChartDataModel,
  GroupedBarChartOptions,
  GroupedBarDataModel,
  TooltipOptions,
  Utils,
} from 'marketpro-design-lib';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../../../auth/services/auth.service';
import { AgeGroup, allAgeGroupsLabel } from '../../models/age-group.model';
import { GroupedResponse } from '../../models/grouped-statistics-response.model';
import { PartialStatisticsResponse } from '../../models/partial-statistics-response.model';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { DataFormat, DataService } from '../../services/data.service';
import { CitiesService } from '../../services/cities.service';
import { CitiesAgeBreakdown } from '../../models/cities-data.model';
import { StatisticsService } from '../../services/statistics.service';
import { IncomeGroup } from '../../models/income-group.model';
import { debounceTimer } from 'projects/marketpro-design-lib/src';

@Component({
  selector: 'mpro-age-and-gender',
  templateUrl: './age-and-gender.component.html',
  styleUrls: ['./age-and-gender.component.scss'],
})
export class AgeAndGenderComponent implements OnInit, OnDestroy {
  public area: string = 'The World';

  private maleColor: Color = {
    name: ColorNames.Green,
    shade: ColorShades.Shade600,
  };
  private femaleColor: Color = {
    name: ColorNames.Fuchsia,
    shade: ColorShades.Shade600,
  };
  public doughnutData: DoughnutDataModel[] = [];
  public chartData: GroupedBarChartDataModel[] = [];
  public groupedChartOptions: GroupedBarChartOptions = {
    colors: [this.femaleColor, this.maleColor],
    dataAxisStep: 5,
    tickFormat: (tick) => `${tick}%`,
  };
  static groupedBarChartTotals: Map<string, number> = new Map();
  public femaleLeft: Box = new Box('', 'Females of all ages');
  public femaleRight: Box = new Box('', '');
  public maleLeft: Box = new Box('', 'Males of all ages');
  public maleRight: Box = new Box('', '');
  public totalLeft: Box = new Box('', 'Total people');
  public totalRight: Box = new Box('', '');
  public selectedSegment?: string;
  public selectedAgeGroup?: string;
  public dataFilters: DataFilters = new DataFilters();
  private ngUnsubscribe: Subject<void> = new Subject();
  public loadingCitiyAgeGender = false;
  private serviceData;
  constructor(
    public dataService: DataService,
    private dataFiltersService: DataFiltersService,
    private authService: AuthService,
    private citiesService: CitiesService,
    private statisticsService: StatisticsService
  ) {}

  public ngOnInit(): void {
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => {
        this.dataFilters = filters;
        const selectedAgeGroup =
          this.dataFilters.ageGroup.compactDisplayValue() === allAgeGroupsLabel
            ? undefined
            : this.dataFilters.ageGroup.compactDisplayValue();
        this.groupedChartOptions = {
          ...this.groupedChartOptions,
          selectedGroup: selectedAgeGroup,
          selectedSubgroup: this.dataFilters.gender,
        };
        if (this.dataFilters.spendingGroup instanceof IncomeGroup) {
          this.statisticsService.getAgeGenderComposition$.next();
        }
        if (this.dataFilters.regionSelection.isCitySelected()) {
          this.citiesService.getCitiyAgeAndGender$.next({
            city: this.dataFilters?.regionSelection?.getName(),
          });
        }
      });
    this.dataService
      .getData()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        // handle data
        this.serviceData = data;
        if (!this.dataFilters.regionSelection.isCitySelected()) {
          this.calculateData(data);
        }
      });
    this.getCityAgeAndGender();
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((plan) => {
        if (plan.isFreemium) {
          this.getAgeAndGenderComposition();
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getAgeAndGenderComposition() {
    this.statisticsService.getAgeGenderComposition$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(debounceTimer),
        switchMap(() => {
          this.loadingCitiyAgeGender = true;
          return this.statisticsService
            .getAgeGenderComposition()
            .pipe(tap(() => (this.loadingCitiyAgeGender = false)));
        })
      )
      .subscribe((response) => {
        const res = response.incomeData;
        this.calculateCitiesData(res);
      });
  }

  private getCityAgeAndGender() {
    this.citiesService.getCitiyAgeAndGender$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(debounceTimer),
        switchMap(() => {
          this.loadingCitiyAgeGender = true;
          return this.statisticsService
            .getAgeGenderComposition()
            .pipe(tap(() => (this.loadingCitiyAgeGender = false)));
        })
      )
      .subscribe((response) => {
        const res = response.incomeData;
        this.calculateCitiesData(res);
      });
  }

  private getAgeGroupFromKey(key: string) {
    const [start, end] = key.split('-');

    return new AgeGroup(+start || undefined, +end || undefined);
  }

  calculateCitiesData(data: CitiesAgeBreakdown) {
    this.doughnutData = [];
    this.chartData = [];
    this.doughnutData.push(
      new DoughnutDataModel(
        data.percentageMale,
        'male',
        Utils.getColorHex(this.maleColor)
      )
    );
    this.doughnutData.push(
      new DoughnutDataModel(
        data.percentageFemale,
        'female',
        Utils.getColorHex(this.femaleColor)
      )
    );

    data.ageGenderCompositions.map((age) => {
      const buildTooltip = (gender, genderValue, genderPercentage) => {
        const tooltip: TooltipOptions = {
          type: 'chart',
          title: `${gender === 'male' ? 'Males' : 'Females'} ${age.ageGroup}`,
          chartText: `${Utils.formatNumber(genderValue, false, '.0-0')} people`,
          chartCaption: `${Utils.formatNumber(
            genderPercentage
          )}% of ${Utils.formatNumber(age.totalPopulation, true)}`,
        };
        return tooltip;
      };
      const bars = [
        new GroupedBarDataModel(
          'FEMALE',
          age.totalAgeFemalePercentage,
          buildTooltip(
            'female',
            age.totalAgeFemalePopulation,
            age.totalAgeFemalePercentage
          )
        ),
        new GroupedBarDataModel(
          'MALE',
          age.totalAgeMalePercentage,
          buildTooltip(
            'male',
            age.totalAgeMalePopulation,
            age.totalAgeMalePercentage
          )
        ),
      ];
      this.chartData.push(new GroupedBarChartDataModel(age.ageGroup, bars));
    });

    this.femaleLeft.middle = Utils.formatNumber(
      data.ageGenderBreakdown.totalAllAgesFemalePopulation,
      false,
      '.0-0'
    );
    this.maleLeft.middle = Utils.formatNumber(
      data.ageGenderBreakdown.totalAllAgesMalePopulation,
      false,
      '.0-0'
    );
    this.totalLeft.middle = Utils.formatNumber(
      data.ageGenderBreakdown.totalPopulation,
      false,
      '.0-0'
    );

    this.femaleRight.middle = `${Utils.formatNumber(
      data.ageGenderBreakdown.totalAllAgesFemalePercentage
    )}%`;
    this.maleRight.middle = `${Utils.formatNumber(
      data.ageGenderBreakdown.totalAllAgesMalePercentage
    )}%`;

    let totalPeople = data.ageGenderBreakdown.totalPopulation;
    let totalPeopleText = `of ${Utils.formatNumber(
      totalPeople,
      true
    )} total people`;
    let totalPeopleFilteredText = `of ${Utils.formatNumber(
      data.ageGenderBreakdown.totalPopulation,
      true
    )} people`;
    this.femaleRight.bottom = totalPeopleFilteredText;
    this.maleRight.bottom = totalPeopleFilteredText;
    this.totalRight.bottom = totalPeopleText;

    let filtedPeoplePercentOfTotalPeople = this.percentage(
      data.ageGenderBreakdown.totalPopulation,
      totalPeople
    );
    this.totalRight.middle = `${Utils.formatNumber(
      filtedPeoplePercentOfTotalPeople,
      false,
      '.2'
    )}%`;
  }

  private calculateData(data: DataFormat) {
    if (
      !data.worldData.total ||
      !data.ageData.current ||
      Object.keys(data.ageData.current).length === 0
    ) {
      return;
    }

    const maleData = this.processGender(
      data.ageData.current[this.dataFilters.year]['1']
    );
    const femaleData = this.processGender(
      data.ageData.current[this.dataFilters.year]['2']
    );

    this.chartData = [];

    Object.keys(maleData.genderData)
      .sort((a, b) => {
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      })
      .forEach((key) => {
        const generateBar = (gender: 'male' | 'female') => {
          const data = gender === 'male' ? maleData : femaleData;
          const tooltip: TooltipOptions = {
            type: 'chart',
            title: `${gender === 'male' ? 'Males' : 'Females'} ${key}`,
            chartText: `${Utils.formatNumber(
              data.genderData[key].value,
              false,
              '.0-0'
            )} people`,
            chartCaption: `${Utils.formatNumber(
              data.genderData[key].percentage
            )}% of ${Utils.formatNumber(data.total, true)}`,
          };

          return new GroupedBarDataModel(
            gender,
            data.genderData[key].percentage,
            tooltip
          );
        };

        const genderData: GroupedBarDataModel[] = [];
        if (!this.authService.isLoggedIn()) {
          genderData.push(generateBar('female'));
          genderData.push(generateBar('male'));
        }

        this.chartData.push(new GroupedBarChartDataModel(key, genderData));
      });

    AgeAndGenderComponent.groupedBarChartTotals.clear();
    AgeAndGenderComponent.groupedBarChartTotals.set('male', maleData.total);
    AgeAndGenderComponent.groupedBarChartTotals.set('female', femaleData.total);

    let total = maleData.total + femaleData.total;
    let malePercent = this.percentage(maleData.total, total);
    let femalePercent = this.percentage(femaleData.total, total);
    this.doughnutData = [];
    this.doughnutData.push(
      new DoughnutDataModel(
        malePercent,
        'male',
        Utils.getColorHex(this.maleColor)
      )
    );
    this.doughnutData.push(
      new DoughnutDataModel(
        femalePercent,
        'female',
        Utils.getColorHex(this.femaleColor)
      )
    );

    this.femaleLeft.middle = Utils.formatNumber(
      femaleData.total,
      false,
      '.0-0'
    );
    this.maleLeft.middle = Utils.formatNumber(maleData.total, false, '.0-0');
    this.totalLeft.middle = Utils.formatNumber(total, false, '.0-0');

    this.femaleRight.middle = `${Utils.formatNumber(femalePercent)}%`;
    this.maleRight.middle = `${Utils.formatNumber(malePercent)}%`;

    let totalPeople = data.worldData.total[this.dataFilters.year].headCount;
    let totalPeopleText = `of ${Utils.formatNumber(
      totalPeople,
      true
    )} total people`;
    let totalPeopleFilteredText = `of ${Utils.formatNumber(
      total,
      true
    )} people`;
    this.femaleRight.bottom = totalPeopleFilteredText;
    this.maleRight.bottom = totalPeopleFilteredText;
    this.totalRight.bottom = totalPeopleText;

    let filtedPeoplePercentOfTotalPeople = this.percentage(total, totalPeople);
    this.totalRight.middle = `${Utils.formatNumber(
      filtedPeoplePercentOfTotalPeople
    )}%`;
  }

  private processGender(data: GroupedResponse<PartialStatisticsResponse>): {
    total: number;
    genderData: { [key: string]: { value: number; percentage: number } };
  } {
    const total = Object.keys(data).reduce((sum: number, key: string) => {
      return (sum += data[key].headCount);
    }, 0);
    const genderData = Object.keys(data).reduce((initial, key) => {
      initial[this.getGroupName(key)] = {
        value: data[key].headCount,
        percentage: +Utils.formatNumber(
          this.percentage(data[key].headCount, total)
        ),
      };

      return initial;
    }, {});

    return { total: total, genderData };
  }

  private percentage(partialValue, totalValue) {
    return (100 * partialValue) / totalValue;
  }

  // Will fix this from backend to return the exact names, this is workaround for now until the backend fix is live.
  private getGroupName(field: string) {
    if (field.startsWith('65-null')) {
      return '>65';
    }

    return field;
  }
}
